import {DeepRequired} from 'ts-essentials'
import {Chat} from './chat'
import {ChatMessageJSON, isUserMentionedInMessage} from './chat-message'
import {DataObjectId} from './data-object'
import {HubJSON} from './hubs'
import {getMessageAssignIds} from './sticker'
import {SystemMessageUtils} from './system-message-utils'

export const notificationChannels = ['chat', 'sound', 'push', 'mobile'] as const
export type NotificationChannel = (typeof notificationChannels)[number]

export type NotificationChannelSettings = {
  [channel in NotificationChannel]?: boolean
}

export const taskNotificationActions = [
  'selfAssigned',
  'otherAssigned',
  'gtd',
  'move',
  'deadline',
  'othersDeadline',
  'newMessage',
  'newMention'
] as const

export type TaskNotificationAction = (typeof taskNotificationActions)[number]

export const chatNotificationActions = [
  'groupChatMessage',
  'mentionInGroupChat',
  'messageInChat'
] as const

export type ChatNotificationAction = (typeof chatNotificationActions)[number]

export type AllNotificationActions =
  | TaskNotificationAction
  | ChatNotificationAction

export type OldNotificationSettings = {
  task?: {
    [action in TaskNotificationAction]?: NotificationChannelSettings
  }
}

export type NotificationSettings = {
  [action in AllNotificationActions]?: NotificationChannelSettings
} & OldNotificationSettings

type DefaultNotificationSettings = {
  [action in AllNotificationActions]: NotificationChannelSettings
}

export const defaultNotificationSettings: DeepRequired<DefaultNotificationSettings> =
  {
    selfAssigned: {sound: true, push: true, chat: true, mobile: true},
    otherAssigned: {sound: false, push: false, chat: false, mobile: false},
    gtd: {sound: false, push: false, chat: false, mobile: false},
    move: {sound: false, push: false, chat: false, mobile: false},
    deadline: {sound: true, push: true, chat: true, mobile: true},
    othersDeadline: {sound: false, push: false, chat: false, mobile: false},
    newMessage: {sound: true, push: true, chat: true, mobile: true},
    newMention: {sound: true, push: true, chat: true, mobile: true},
    groupChatMessage: {sound: true, push: true, chat: true, mobile: true},
    mentionInGroupChat: {sound: true, push: true, chat: true, mobile: true},
    messageInChat: {sound: true, push: true, chat: true, mobile: true}
  }

export type DisabledNotificationSettings = {
  [action in AllNotificationActions]?: NotificationChannel[]
}

export const disabledNotificationSettings: DisabledNotificationSettings = {
  newMessage: ['chat'],
  newMention: ['chat'],
  groupChatMessage: ['chat'],
  mentionInGroupChat: ['chat'],
  messageInChat: ['chat']
}

interface HowToNotify {
  sound: boolean
  push: boolean
  mobilePush: boolean
}
const noNotify: Readonly<HowToNotify> = {
  sound: false,
  push: false,
  mobilePush: false
}
const allNotify: Readonly<HowToNotify> = {
  sound: true,
  push: true,
  mobilePush: true
}

export const getNotificationSetting = (
  settings: NotificationSettings,
  action: AllNotificationActions,
  channel: NotificationChannel
): boolean => {
  const isMainChannel = channel === 'chat'
  if (isMainChannel) {
    if (disabledNotificationSettings[action]?.includes(channel)) {
      return true
    }
    return Object.values(
      settings?.[action] ?? defaultNotificationSettings[action]
    ).some(Boolean)
  }
  return (
    settings?.[action]?.[channel] ??
    defaultNotificationSettings[action][channel]
  )
}

export const howToNotifyOnAction = (
  settings: NotificationSettings,
  action: AllNotificationActions
): HowToNotify => {
  const sound = getNotificationSetting(settings, action, 'sound')
  const push = getNotificationSetting(settings, action, 'push')
  const mobilePush = getNotificationSetting(settings, action, 'mobile')

  return {sound, push, mobilePush}
}

export const howToNotifyOnMessage = (
  settings: NotificationSettings,
  userId: DataObjectId,
  hub: HubJSON,
  msg: ChatMessageJSON,
  assigned: DataObjectId[]
): HowToNotify => {
  if (SystemMessageUtils.isSystemMessage(msg)) {
    if (!SystemMessageUtils.isKnownSystemMessage(msg)) {
      return noNotify
    }

    const type = SystemMessageUtils.getType(msg)
    const actionBy = msg?.properties?.actionBy

    if (type === 'deadlineNotice') {
      if (assigned.includes(userId)) {
        return allNotify
      }
    } else if (type === 'deadline') {
      const myDeadline = assigned.includes(userId)
      const action = myDeadline ? 'deadline' : 'othersDeadline'
      return howToNotifyOnAction(settings, action)
    } else if (type === 'assigned' && actionBy !== userId) {
      const assigned = getMessageAssignIds(msg)
      const hasSelf = assigned.includes(userId)
      const action = hasSelf ? 'selfAssigned' : 'otherAssigned'
      return howToNotifyOnAction(settings, action)
    } else if (type === 'gtd' && actionBy !== userId) {
      return howToNotifyOnAction(settings, 'gtd')
    } else if (type === 'move' && actionBy !== userId) {
      return howToNotifyOnAction(settings, 'move')
    }
  } else {
    const mention = isUserMentionedInMessage(userId, msg)
    const {isTaskChat, isDirect} = Chat.chatType(hub)

    let action: AllNotificationActions

    if (mention) {
      action = isTaskChat ? 'newMention' : 'mentionInGroupChat'
    } else {
      if (isTaskChat) {
        action = 'newMessage'
      } else if (isDirect) {
        action = 'messageInChat'
      } else {
        action = 'groupChatMessage'
      }
    }

    return howToNotifyOnAction(settings, action)
  }

  return noNotify
}

export const isTaskNotificationAction = (
  action: AllNotificationActions | 'task'
): action is TaskNotificationAction => {
  return taskNotificationActions.includes(action as TaskNotificationAction)
}
